'use client';

import { useState, useEffect } from 'react';
import { ChevronDown, Check, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/data-display/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/navigation/dropdown-menu';
import { cn } from '@/lib/utils';
import { contactStatusOptions, relationshipStatusConfig } from '@/lib/relationship-statuses';
import type { RelationshipStatus } from '@/types/report-types';

export type ContactStatusPickerProps = {
  contactId: string;
  status?: RelationshipStatus;
  onStatusChange?: (status: RelationshipStatus) => void;
  onStatusUpdate?: (status: RelationshipStatus) => Promise<void> | void;
  onError?: (message: string) => void;
  className?: string;
  disabled?: boolean;
  defaultOpen?: boolean;
  /**
   * Display mode for the badge:
   * - 'regular': Shows icon, label, and dropdown arrow
   * - 'minimal': Shows only icon and dropdown arrow
   */
  mode?: 'regular' | 'minimal';
};

function ContactStatusPicker({
  contactId,
  status,
  onStatusChange,
  onStatusUpdate,
  onError,
  className,
  disabled,
  defaultOpen = false,
  mode = 'regular',
}: ContactStatusPickerProps) {
  const [currentStatus, setCurrentStatus] = useState<RelationshipStatus>(status ?? 'not_contacted');
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status) {
      setCurrentStatus(status);
    }
  }, [status]);

  useEffect(() => {
    if (defaultOpen) {
      setIsOpen(true);
    }
  }, [defaultOpen]);

  const config = relationshipStatusConfig[currentStatus] ?? relationshipStatusConfig.not_contacted;

  const handleStatusSelect = async (nextStatus: RelationshipStatus) => {
    if (nextStatus === currentStatus || isUpdating) {
      setIsOpen(false);
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      if (onStatusUpdate) {
        await onStatusUpdate(nextStatus);
      } else {
        const response = await fetch('/api/hub/contacts', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contactId, status: nextStatus }),
          cache: 'no-store',
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.error || 'Unable to update status');
        }
      }

      setCurrentStatus(nextStatus);
      onStatusChange?.(nextStatus);
      setIsOpen(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update status';
      setError(message);
      onError?.(message);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center focus-visible:outline-none focus-visible:ring focus-visible:ring-primary/40 disabled:pointer-events-none',
            className
          )}
          disabled={disabled || isUpdating}
          onClick={(event) => event.stopPropagation()}
        >
          <Badge
            variant="outline"
            className={cn(
              'flex items-center gap-1.5 border border-border bg-card text-foreground transition duration-150',
              mode === 'minimal'
                ? 'h-8 px-2'
                : 'h-8 px-2.5 text-[9px] font-semibold tracking-[0.08em]',
              config.bgColor,
              config.color
            )}
          >
            {isUpdating ? (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            ) : (
              <config.icon className={cn('h-3 w-3', config.color)} />
            )}
            {mode === 'regular' && <span className="leading-none">{config.label}</span>}
            <ChevronDown
              className={cn('h-3 w-3 transition-transform duration-150', {
                'rotate-180': isOpen,
              })}
            />
          </Badge>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-[13rem]">
        {contactStatusOptions.map((option) => {
          const optionConfig = relationshipStatusConfig[option];
          const isActive = option === currentStatus;
          return (
            <DropdownMenuItem
              key={option}
              onSelect={(event) => {
                event.preventDefault();
                void handleStatusSelect(option);
              }}
              selected={isActive}
              className={cn('cursor-pointer', !isActive && 'text-muted-foreground')}
              disabled={isUpdating}
            >
              <optionConfig.icon className={`h-3 w-3 ${optionConfig.color}`} />
              <span className="flex-1 text-left">{optionConfig.label}</span>
              {isActive && <Check className="h-3 w-3" />}
            </DropdownMenuItem>
          );
        })}
        {error && <p className="mt-1 px-3 text-xs text-destructive">{error}</p>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { ContactStatusPicker };
export default ContactStatusPicker;
